import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

// Up to 8 image inputs for brand style extraction
const BRAND_STYLE_IMAGE_SLOTS = Array.from({ length: 8 }, (_, index) => ({
  type: "image" as const,
  allowedFormats: ["image/jpeg", "image/png", "image/webp"],
  label: `Image ${index + 1}`,
  slot: `brand_image_${index + 1}`,
  group: "brand_images",
  required: index === 0, // At least one image required
}));

const extractBrandStyleParams = z
  .object({
    attachments: z.array(attachment).optional(),
  })
  .strip();

type ExtractBrandStyleParams = z.infer<typeof extractBrandStyleParams>;

export const extractBrandStyleModel = defineModel({
  key: "extract-brand-style",
  stepType: "text",
  spec: {
    label: "Extract Brand Style",
    description:
      "Extract brand style information from images as structured JSON. Analyzes visual elements, colors, typography, and design patterns.",
    icon: "chat-icon.svg",
    tags: ["text-gen", "brand", "vision"],
    featured: true,
    features: { attachments: true } as const,
    options: {
      attachments: BRAND_STYLE_IMAGE_SLOTS,
    },
    params: extractBrandStyleParams,
  },
  providers: [
    {
      provider: "openrouter",
      modelId: "extract-brand-style",
      estimateCost(params: ExtractBrandStyleParams) {
        // Single LLM call with all images — base cost + per-image token cost
        const imageCount =
          params.attachments?.filter((file) =>
            file.slot.startsWith("brand_image_"),
          ).length ?? 1;
        // ~$0.02 base + $0.005 per image (single multi-image call)
        return (0.02 + 0.005 * imageCount) * 1_000_000 * (1 + BRAND_MARKUP);
      },
      toPayload(params: ExtractBrandStyleParams) {
        // Extract image URLs from attachments
        const imageUrls =
          params.attachments
            ?.filter((file) => file.slot.startsWith("brand_image_"))
            .map((file) => file.data ?? file.url)
            .filter((url): url is string => Boolean(url)) ?? [];

        return {
          // Empty prompt - the extraction prompt is hardcoded in the backend
          prompt: "",
          imageUrls,
        };
      },
    },
  ],
});
